// Router principal y guard de auth/rol.
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { useEffect, type ReactNode } from 'react'
import { Capacitor } from '@capacitor/core'
import { useAuth, useLoadProfile } from '@/hooks/useAuth'
import { usePushRegistration } from '@/hooks/usePush'
import { ShiftBeacon } from '@/components/ShiftBeacon'
import { PushToast } from '@/components/PushToast'
import { WebUpdateBanner } from '@/components/WebUpdateBanner'
import type { Role } from '@/types'
import { AppLayout } from '@/components/AppLayout'
import Login from '@/pages/auth/Login'
import DeliveryList from '@/pages/deliveries/DeliveryList'
import DeliveryDetail from '@/pages/deliveries/DeliveryDetail'
import PlannedBoard from '@/pages/deliveries/PlannedBoard'
import UsersList from '@/pages/users/UsersList'
import LiveMap from '@/pages/map/LiveMap'
import QuoteList from '@/pages/quotes/QuoteList'
import QuoteNew from '@/pages/quotes/QuoteNew'

// Autenticado en Google pero sin usuario local (no provisionado).
function NoAccess() {
  const { logout } = useAuth()
  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-4 p-8 text-center">
      <h1 className="text-xl font-bold text-gray-900">Sin acceso</h1>
      <p className="max-w-sm text-sm text-gray-500">
        Tu cuenta aún no tiene acceso. Pide a un administrador que te dé de alta.
      </p>
      <button
        onClick={logout}
        className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-200"
      >
        Cerrar sesión
      </button>
    </div>
  )
}

function RequireAuth({ children, roles }: { children: ReactNode; roles?: Role[] }) {
  const { isAuthenticated, isLoading, user } = useAuth()
  if (isLoading) return <div className="p-8 text-center text-gray-500">Cargando…</div>
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (!user) return <NoAccess />
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />
  return <>{children}</>
}

function Guard({ roles, children }: { roles?: Role[]; children: ReactNode }) {
  return (
    <RequireAuth roles={roles}>
      <AppLayout>{children}</AppLayout>
    </RequireAuth>
  )
}

// Botón "atrás" de Android: navegar dentro de la app; en la raíz, minimizar.
const ROOT_PATHS = new Set(['/', '/login', '/entregas', '/cotizaciones'])

function AndroidBackHandler() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    let remove: (() => void) | undefined
    import('@capacitor/app')
      .then(({ App: CapApp }) =>
        CapApp.addListener('backButton', () => {
          if (ROOT_PATHS.has(window.location.pathname)) CapApp.minimizeApp()
          else navigate(-1)
        }),
      )
      .then((h) => { if (h) remove = () => h.remove() })
      .catch(() => { /* plugin ausente */ })
    return () => remove?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  void pathname
  return null
}

// El vendedor aterriza en Cotizaciones; el resto en Entregas.
function Home() {
  const { user } = useAuth()
  return <Navigate to={user?.role === 'vendedor' ? '/cotizaciones' : '/entregas'} replace />
}

const OPS: Role[] = ['admin', 'almacen', 'repartidor']

export default function App() {
  useLoadProfile()
  usePushRegistration()
  return (
    <>
      <ShiftBeacon />
      <AndroidBackHandler />
      <PushToast />
      <WebUpdateBanner />
      <Routes>
        <Route path="/login" element={<Login />} />

        <Route path="/" element={<RequireAuth><Home /></RequireAuth>} />
        <Route path="/entregas" element={<Guard roles={OPS}><DeliveryList /></Guard>} />
        <Route path="/salidas" element={<Guard roles={['admin', 'almacen']}><PlannedBoard /></Guard>} />
        <Route path="/entregas/:id" element={<Guard roles={OPS}><DeliveryDetail /></Guard>} />

        <Route path="/cotizaciones" element={<Guard roles={['admin', 'vendedor']}><QuoteList /></Guard>} />
        <Route path="/cotizaciones/nueva" element={<Guard roles={['admin', 'vendedor']}><QuoteNew /></Guard>} />

        <Route path="/mapa" element={<Guard roles={['admin', 'almacen']}><LiveMap /></Guard>} />
        <Route path="/usuarios" element={<Guard roles={['admin']}><UsersList /></Guard>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  )
}
